import React, { useEffect, useMemo, useRef, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AttachFileRoundedIcon from '@material-ui/icons/AttachFileRounded';
import InsertDriveFileRoundedIcon from '@material-ui/icons/InsertDriveFileRounded';
import SendRoundedIcon from '@material-ui/icons/SendRounded';

import DataService from '../../services/DataService';
import { useAuthContext } from '../../services/AuthService';
import { MetricsPanel } from './MetricsPanel';
import { StepUpPrompt } from './StepUpPrompt';
import { ValidationReport } from './ValidationReport';
import {
    getLastRunEventSequence,
    isAgentEventStreamEnabled,
    isAgentWorkspaceEnabled,
    isTrainingEnabled,
    mergeRunEvents,
    workflowStatusLabel,
} from './workflow';

const styles = (theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
    },
    scrollArea: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 3, 1),
    },
    requirements: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    fields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: theme.spacing(1.5),
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    plan: {
        margin: 0,
        padding: theme.spacing(2),
        overflowX: 'auto',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.type === 'dark' ? '#151b24' : '#f6f8fa',
        fontSize: '0.8rem',
        whiteSpace: 'pre-wrap',
    },
    statusList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    statusRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    error: {
        color: theme.palette.error.main,
    },
    muted: {
        color: theme.palette.text.secondary,
    },
    metricFigure: {
        margin: 0,
        minWidth: 0,
    },
    stepUp: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        border: `1px solid ${theme.palette.warning.main}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
    },
    composer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: 48,
        boxSizing: 'border-box',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1, 0.5, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        '& .MuiTextField-root': {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiInputBase-root': {
            width: '100%',
            alignItems: 'center',
            padding: 0,
        },
        '& .MuiInputBase-inputMultiline': {
            padding: theme.spacing(0.75, 0),
            lineHeight: 1.5,
        },
    },
    composerShell: {
        position: 'relative',
        width: '100%',
    },
    mentionMenu: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 'calc(100% + 8px)',
        zIndex: 3,
        padding: theme.spacing(0.75),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[6],
        maxHeight: 280,
        overflowY: 'auto',
    },
    mentionOption: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1),
        textTransform: 'none',
        textAlign: 'left',
        borderRadius: theme.spacing(1),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    mentionOptionActive: {
        backgroundColor: theme.palette.action.selected,
    },
    mentionIcon: {
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    mentionDetails: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    mentionName: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    composerIcon: {
        marginBottom: theme.spacing(0.25),
    },
    composerArea: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 3, 1),
        backgroundColor: 'transparent',
    },
});

const createRequestKey = (prefix) => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const stableConversationRequestKey = (conversationId) => {
    const safeConversationId = String(conversationId || '')
        .replace(/[^a-zA-Z0-9._:-]/g, '-')
        .slice(0, 96);
    return `conversation-${safeConversationId || 'default'}`;
};

const decodeObject = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
    }
    if (typeof value !== 'string') {
        return null;
    }
    try {
        const decoded = JSON.parse(value);
        return decoded && typeof decoded === 'object' && !Array.isArray(decoded) ? decoded : null;
    } catch (_error) {
        return null;
    }
};

const reauthenticate = async (password) => {
    // Defer AuthService so legacy workflow tests and chat do not load Axios' browser ESM entrypoint.
    // eslint-disable-next-line global-require
    const authModule = require('../../services/AuthService');
    const AuthService = authModule.default || authModule;
    return AuthService.Reauthenticate(password);
};

const startGoogleReauthentication = () => {
    // Defer AuthService for the same reason as the password path above.
    // eslint-disable-next-line global-require
    const authModule = require('../../services/AuthService');
    const AuthService = authModule.default || authModule;
    return AuthService.GoogleLogin({ returnTo: '/pinns-agent', forceReauth: true });
};

const terminalRunStates = new Set(['COMPLETED', 'TIMED_OUT', 'CANCELLED', 'FAILED', 'CLEANUP_FAILED']);
const MAX_EVENT_STREAM_RECONNECTS = 3;

const artifactRetentionLabel = (retentionExpiresAt) => {
    if (!retentionExpiresAt) {
        return 'Retention: policy-managed';
    }
    const expiry = new Date(retentionExpiresAt);
    if (Number.isNaN(expiry.getTime())) {
        return 'Retention: policy-managed';
    }
    return `Retained until ${expiry.toISOString().slice(0, 10)}`;
};

const safeArtifactText = (value, maxLength) => {
    if (typeof value !== 'string' || value.length === 0 || value.length > maxLength || /[\u0000-\u001f\u007f]/.test(value)) {
        return '';
    }
    return value.trim();
};

const artifactPresentation = (artifact) => {
    const kind = safeArtifactText(artifact?.kind, 64) || 'artifact';
    const filenameCandidate = safeArtifactText(artifact?.filename, 128);
    const filename = filenameCandidate && !['.', '..'].includes(filenameCandidate) && !/[\\/]/.test(filenameCandidate)
        ? filenameCandidate
        : '';
    return {
        label: filename || `${kind} artifact`,
        caption: safeArtifactText(artifact?.caption, 256),
    };
};

const BUDGET_FIELDS = [
    ['max_minutes', 'Maximum runtime', 'minutes'],
    ['max_epochs', 'Maximum epochs', 'epochs'],
    ['max_collocation_points', 'Maximum collocation points', 'points'],
    ['max_dataset_bytes', 'Maximum dataset', 'bytes'],
    ['max_artifact_bytes', 'Maximum artifact output', 'bytes'],
    ['max_log_bytes', 'Maximum logs', 'bytes'],
    ['max_model_calls', 'Maximum model calls', 'calls'],
    ['max_output_tokens', 'Maximum output tokens', 'tokens'],
    ['max_storage_bytes', 'Maximum plan storage', 'bytes'],
    ['max_concurrent_runs', 'Maximum concurrent runs', 'runs'],
    ['max_cpu_cores', 'Maximum CPU cores', 'cores'],
    ['max_memory_mb', 'Maximum memory', 'MB'],
    ['max_cost_usd', 'Maximum estimated cost', 'USD'],
];
const MAX_PLAN_PREVIEW_CHARS = 20_000;
const PLAN_PREVIEW_TRUNCATION = '\n... plan preview truncated';

const budgetPresentation = (budget) => {
    if (!budget || typeof budget !== 'object' || Array.isArray(budget)) {
        return [];
    }
    return BUDGET_FIELDS.flatMap(([key, label, unit]) => {
        const value = budget[key];
        const minimum = key === 'max_cost_usd' ? 0 : Number.MIN_VALUE;
        if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > 10_000_000_000) {
            return [];
        }
        return [{ key, label, unit, value }];
    });
};

const planPreview = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return 'Plan preview unavailable.';
    }
    try {
        const serialized = JSON.stringify(value, null, 2);
        if (serialized.length <= MAX_PLAN_PREVIEW_CHARS) {
            return serialized;
        }
        return `${serialized.slice(0, MAX_PLAN_PREVIEW_CHARS - PLAN_PREVIEW_TRUNCATION.length)}${PLAN_PREVIEW_TRUNCATION}`;
    } catch (_error) {
        return 'Plan preview unavailable.';
    }
};

const AgentWorkflowPanel = ({
    classes,
    conversationId,
    initialServerConversationId = '',
    onServerConversationIdChange,
    availableFiles = [],
    onFilesChanged,
}) => {
    const auth = useAuthContext();
    const isAuthenticated = !!auth?.state?.isAuthenticated;
    const paymentPlan = auth?.state?.payment_plan || '';
    const authMethod = auth?.state?.auth_method || 'password';
    const [requirements, setRequirements] = useState('');
    const [serverConversationId, setServerConversationId] = useState(initialServerConversationId);
    const [plan, setPlan] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const [workspaceValidation, setWorkspaceValidation] = useState(null);
    const [approval, setApproval] = useState(null);
    const [run, setRun] = useState(null);
    const [result, setResult] = useState(null);
    const [events, setEvents] = useState([]);
    const [artifacts, setArtifacts] = useState([]);
    const [artifactDownloadId, setArtifactDownloadId] = useState('');
    const [uploads, setUploads] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    const [approvalFields, setApprovalFields] = useState({
        code_hash: '',
        config_hash: '',
        dataset_hash: '',
        image_digest: '',
        bounds: '{"max_minutes": 5}',
    });
    const [isPlanning, setIsPlanning] = useState(false);
    const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
    const [isCreatingConversation, setIsCreatingConversation] = useState(false);
    const [isExportingConversation, setIsExportingConversation] = useState(false);
    const [isDeletingConversation, setIsDeletingConversation] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [isStepUpRequired, setIsStepUpRequired] = useState(false);
    const [isStepUpSubmitting, setIsStepUpSubmitting] = useState(false);
    const [stepUpComplete, setStepUpComplete] = useState(false);
    const [stepUpError, setStepUpError] = useState('');
    const [planEntitlements, setPlanEntitlements] = useState(null);
    const [error, setError] = useState('');
    const conversationIdempotencyKey = useRef(createRequestKey('conversation'));
    const eventCursor = useRef(0);
    const eventRunId = useRef(null);
    const resultRunId = useRef(null);
    const composerInputRef = useRef(null);
    const [eventStreamFailed, setEventStreamFailed] = useState(false);
    const eventStreamEnabled = isAgentEventStreamEnabled();
    const estimatedBudget = budgetPresentation(
        plan && typeof plan.plan === 'object' && !Array.isArray(plan.plan)
            ? plan.plan.estimated_budget
            : plan?.estimated_budget,
    );
    const planUpperBounds = budgetPresentation(planEntitlements);
    const mentionSuggestions = useMemo(() => {
        if (mentionQuery === null) {
            return [];
        }
        const normalizedQuery = mentionQuery.toLowerCase();
        return availableFiles
            .filter((file) => (
                typeof file?.id === 'string'
                && typeof file?.filename === 'string'
                && file.filename.toLowerCase().startsWith(normalizedQuery)
            ))
            .slice(0, 8);
    }, [availableFiles, mentionQuery]);

    const updateApprovalField = (field) => (event) => {
        setApprovalFields((current) => ({ ...current, [field]: event.target.value }));
    };

    useEffect(() => {
        setServerConversationId(initialServerConversationId || '');
        setRequirements('');
        setPlan(null);
        setWorkspace(null);
        setWorkspaceValidation(null);
        setApproval(null);
        setRun(null);
        setResult(null);
        setEvents([]);
        eventCursor.current = 0;
        eventRunId.current = null;
        resultRunId.current = null;
        setEventStreamFailed(false);
        setArtifacts([]);
        setArtifactDownloadId('');
        setUploads([]);
        setMentionQuery(null);
        setMentionIndex(0);
        setError('');
        setIsStepUpRequired(false);
        setIsStepUpSubmitting(false);
        setStepUpComplete(false);
        setStepUpError('');
        conversationIdempotencyKey.current = stableConversationRequestKey(conversationId);
    }, [conversationId]);

    const updateMentionQuery = (value, cursorPosition) => {
        const beforeCursor = value.slice(0, cursorPosition);
        const match = beforeCursor.match(/(?:^|\s)@([^\s@]*)$/);
        if (!match) {
            setMentionQuery(null);
            setMentionIndex(0);
            return;
        }
        setMentionQuery(match[1]);
        setMentionIndex(0);
    };

    const handleRequirementsChange = (event) => {
        const value = event.target.value;
        setRequirements(value);
        updateMentionQuery(value, event.target.selectionStart ?? value.length);
    };

    const selectMention = (file) => {
        const input = composerInputRef.current;
        const value = requirements;
        const cursorPosition = input?.selectionStart ?? value.length;
        const beforeCursor = value.slice(0, cursorPosition);
        const match = beforeCursor.match(/(?:^|\s)@([^\s@]*)$/);
        if (!match) {
            setMentionQuery(null);
            return;
        }
        const mentionStart = cursorPosition - match[1].length - 1;
        const replacement = `@${file.filename} `;
        const nextValue = `${value.slice(0, mentionStart)}${replacement}${value.slice(cursorPosition)}`;
        const nextCursorPosition = mentionStart + replacement.length;
        setRequirements(nextValue);
        setMentionQuery(null);
        setMentionIndex(0);
        window.setTimeout(() => {
            composerInputRef.current?.focus();
            composerInputRef.current?.setSelectionRange(nextCursorPosition, nextCursorPosition);
        }, 0);
    };

    const handleRequirementsKeyDown = (event) => {
        if (mentionQuery === null || !mentionSuggestions.length) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setMentionIndex((current) => (current + 1) % mentionSuggestions.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setMentionIndex((current) => (current - 1 + mentionSuggestions.length) % mentionSuggestions.length);
        } else if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            selectMention(mentionSuggestions[mentionIndex]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setMentionQuery(null);
        }
    };

    useEffect(() => {
        let active = true;
        if (!isAuthenticated) {
            setPlanEntitlements(null);
            return () => {
                active = false;
            };
        }
        DataService.GetUserAccessPlans()
            .then((response) => {
                if (!active) {
                    return;
                }
                const config = response?.data;
                const plans = Array.isArray(config?.plans) ? config.plans : [];
                const planId = paymentPlan || config?.default_plan;
                const selected = plans.find((candidate) => candidate?.id === planId)
                    || plans.find((candidate) => candidate?.id === config?.default_plan);
                setPlanEntitlements(selected?.agent_entitlements || null);
            })
            .catch(() => {
                if (active) {
                    setPlanEntitlements(null);
                }
            });
        return () => {
            active = false;
        };
    }, [isAuthenticated, paymentPlan]);

    const ensureServerConversation = async () => {
        if (serverConversationId) {
            return serverConversationId;
        }
        setIsCreatingConversation(true);
        try {
            const response = await DataService.CreateAgentConversation('PINNs Agent', conversationIdempotencyKey.current);
            const createdConversationId = response?.data?.id;
            if (!createdConversationId) {
                throw new Error('The workflow conversation response was incomplete.');
            }
            setServerConversationId(createdConversationId);
            onServerConversationIdChange?.(conversationId, createdConversationId);
            return createdConversationId;
        } finally {
            setIsCreatingConversation(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated || !serverConversationId || isPlanning) {
            return undefined;
        }
        let active = true;
        DataService.GetAgentConversation(serverConversationId)
            .then((response) => {
                if (!active) {
                    return;
                }
                const data = response?.data || {};
                const latestRequirements = Array.isArray(data.requirements)
                    ? data.requirements[0]
                    : null;
                const requirementPayload = decodeObject(latestRequirements?.payload);
                if (typeof requirementPayload?.text === 'string') {
                    setRequirements(requirementPayload.text);
                }
                if (Array.isArray(requirementPayload?.uploads)) {
                    setUploads(requirementPayload.uploads.flatMap((upload) => {
                        if (!upload || typeof upload !== 'object' || typeof upload.id !== 'string') {
                            return [];
                        }
                        return [{
                            id: `server-${upload.id}`,
                            uploadId: upload.id,
                            name: typeof upload.name === 'string' ? upload.name : 'Uploaded file',
                            status: 'ready',
                            progress: 100,
                        }];
                    }));
                }
                const latestPlan = Array.isArray(data.plans)
                    ? data.plans.find((candidate) => candidate?.status !== 'superseded')
                    : null;
                if (latestPlan?.id) {
                    const planPayload = decodeObject(latestPlan.payload);
                    setPlan({
                        id: latestPlan.id,
                        content_hash: latestPlan.content_hash,
                        status: latestPlan.status,
                        plan: planPayload || {},
                    });
                }
            })
            .catch((requestError) => {
                if (active && requestError?.response?.status !== 404) {
                    setError(requestError?.response?.data?.detail || 'Unable to restore the workflow conversation.');
                }
            });
        return () => {
            active = false;
        };
    }, [isAuthenticated, isPlanning, serverConversationId]);

    const handleCreatePlan = async (event) => {
        event.preventDefault();
        const trimmed = requirements.trim();
        if (!conversationId || !trimmed || isPlanning) {
            return;
        }
        setIsPlanning(true);
        setError('');
        setApproval(null);
        setRun(null);
        setResult(null);
        setWorkspace(null);
        setWorkspaceValidation(null);
        setEvents([]);
        eventCursor.current = 0;
        eventRunId.current = null;
        resultRunId.current = null;
        setEventStreamFailed(false);
        setArtifacts([]);
        setArtifactDownloadId('');
        setIsStepUpRequired(false);
        setStepUpComplete(false);
        setStepUpError('');
        try {
            const durableConversationId = await ensureServerConversation();
            const freshUploadReferences = uploads
                .filter((upload) => upload.uploadId)
                .map((upload) => ({ id: upload.uploadId, name: upload.name }));
            const referencedLibraryFiles = availableFiles
                .filter((file) => requirements.includes(`@${file.filename}`))
                .map((file) => ({ id: file.id, name: file.filename }));
            const uploadReferences = [...freshUploadReferences, ...referencedLibraryFiles]
                .filter((upload, index, references) => references.findIndex((candidate) => candidate.id === upload.id) === index);
            const response = await DataService.CreateAgentPlan(
                durableConversationId,
                trimmed,
                uploadReferences,
                createRequestKey('plan'),
            );
            setPlan(response?.data || null);
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'The workflow planner is unavailable.');
        } finally {
            setIsPlanning(false);
        }
    };

    const handleCreateReviewedWorkspace = async () => {
        if (!isAgentWorkspaceEnabled() || !plan?.id || isCreatingWorkspace) {
            return;
        }
        setIsCreatingWorkspace(true);
        setError('');
        setWorkspaceValidation(null);
        try {
            const response = await DataService.CreateAgentTemplateWorkspace(
                plan.id,
                'tiny_pinn',
                `workspace-${plan.id}-tiny_pinn`,
            );
            const createdWorkspace = response?.data?.workspace || null;
            if (!createdWorkspace?.id) {
                throw new Error('The reviewed workspace response was incomplete.');
            }
            setWorkspace(createdWorkspace);
            const validationResponse = await DataService.ValidateAgentWorkspace(createdWorkspace.id);
            setWorkspaceValidation(validationResponse?.data?.validation || null);
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || requestError?.message || 'The reviewed workspace is unavailable.');
        } finally {
            setIsCreatingWorkspace(false);
        }
    };

    const handleUploadChange = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        for (const file of files) {
            const localId = createRequestKey('upload');
            const idempotencyKey = `agent-${localId}`;
            setUploads((current) => [...current, { id: localId, name: file.name, status: 'uploading', progress: 0 }]);
            try {
                const response = await DataService.UploadAgentFile(file, idempotencyKey, (progressEvent) => {
                    const total = progressEvent.total || file.size || 1;
                    const progress = Math.min(100, Math.round((progressEvent.loaded / total) * 100));
                    setUploads((current) => current.map((item) => (
                        item.id === localId ? { ...item, progress } : item
                    )));
                });
                setUploads((current) => current.map((item) => (
                    item.id === localId
                        ? { ...item, status: response?.data?.status || 'ready', progress: 100, uploadId: response?.data?.id }
                        : item
                )));
                onFilesChanged?.();
            } catch (requestError) {
                setUploads((current) => current.map((item) => (
                    item.id === localId
                        ? { ...item, status: 'rejected', error: requestError?.response?.data?.detail || 'Upload unavailable.' }
                        : item
                )));
            }
        }
    };

    const handleApprovePlan = async () => {
        if (!plan?.id || !approvalFields.code_hash.trim() || !approvalFields.config_hash.trim() || isApproving) {
            return;
        }
        let bounds;
        try {
            bounds = JSON.parse(approvalFields.bounds);
        } catch (parseError) {
            setError('Resource bounds must be valid JSON.');
            return;
        }
        setIsApproving(true);
        setError('');
        try {
            const response = await DataService.ApproveAgentPlan(
                plan.id,
                { ...approvalFields, bounds },
                `approve-${plan.id}-${plan.content_hash}`,
            );
            setApproval(response?.data || null);
            setPlan((current) => ({ ...current, status: 'approved' }));
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'Plan approval is unavailable.');
        } finally {
            setIsApproving(false);
        }
    };

    const submitStartRun = async () => {
        let bounds;
        try {
            bounds = JSON.parse(approvalFields.bounds);
        } catch (parseError) {
            setError('Resource bounds must be valid JSON.');
            return;
        }
        setIsStarting(true);
        setError('');
        try {
            const response = await DataService.CreateAgentRun({
                plan_id: plan.id,
                code_hash: approvalFields.code_hash,
                config_hash: approvalFields.config_hash,
                dataset_hash: approvalFields.dataset_hash,
                image_digest: approvalFields.image_digest,
                bounds,
                idempotency_key: `run-${plan.id}-${approval.fingerprint_hash}`,
            });
            setRun(response?.data || null);
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'The run broker is unavailable.');
        } finally {
            setIsStarting(false);
        }
    };

    const handleStepUp = async (password) => {
        setIsStepUpSubmitting(true);
        setStepUpError('');
        try {
            await reauthenticate(password);
            setStepUpComplete(true);
            setIsStepUpRequired(false);
            await submitStartRun();
        } catch (requestError) {
            setStepUpError(requestError?.response?.data?.detail || requestError?.message || 'Recent password authentication failed.');
        } finally {
            setIsStepUpSubmitting(false);
        }
    };

    const handleGoogleStepUp = () => {
        setIsStepUpSubmitting(true);
        setStepUpError('');
        try {
            startGoogleReauthentication();
        } catch (requestError) {
            setIsStepUpSubmitting(false);
            setStepUpError(requestError?.message || 'Google reauthentication could not be started.');
        }
    };

    const handleStartRun = async () => {
        if (!isTrainingEnabled() || !plan?.id || !approval || isStarting) {
            return;
        }
        if (!stepUpComplete) {
            setStepUpError('');
            setIsStepUpRequired(true);
            return;
        }
        await submitStartRun();
    };

    const handleDownloadArtifact = async (artifact) => {
        if (!run?.id || !artifact?.id || artifactDownloadId) {
            return;
        }
        setArtifactDownloadId(artifact.id);
        setError('');
        try {
            const response = await DataService.DownloadAgentArtifact(run.id, artifact.id);
            const objectUrl = window.URL.createObjectURL(response.data);
            const safeArtifactId = String(artifact.id).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64) || 'artifact';
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = `${safeArtifactId}.bin`;
            anchor.rel = 'noopener';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'Unable to download the artifact.');
        } finally {
            setArtifactDownloadId('');
        }
    };

    const handleExportConversation = async () => {
        if (!serverConversationId || isExportingConversation || isDeletingConversation) {
            return;
        }
        setIsExportingConversation(true);
        setError('');
        try {
            const response = await DataService.ExportAgentConversation(serverConversationId);
            const payload = JSON.stringify(response?.data || {}, null, 2);
            const objectUrl = window.URL.createObjectURL(new Blob([payload], { type: 'application/json' }));
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = 'pinns-agent-conversation.json';
            anchor.rel = 'noopener';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'Unable to export the conversation.');
        } finally {
            setIsExportingConversation(false);
        }
    };

    const handleDeleteConversation = async () => {
        if (!serverConversationId || isExportingConversation || isDeletingConversation) {
            return;
        }
        if (!window.confirm('Delete this server conversation and its stored messages, requirements, and plans?')) {
            return;
        }
        setIsDeletingConversation(true);
        setError('');
        try {
            await DataService.DeleteAgentConversation(serverConversationId);
            setServerConversationId('');
            onServerConversationIdChange?.(conversationId, '');
            setRequirements('');
            setPlan(null);
            setWorkspace(null);
            setWorkspaceValidation(null);
            setApproval(null);
            setRun(null);
            setResult(null);
            setEvents([]);
            setArtifacts([]);
            setUploads([]);
            eventCursor.current = 0;
            eventRunId.current = null;
            resultRunId.current = null;
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'Unable to delete the conversation.');
        } finally {
            setIsDeletingConversation(false);
        }
    };

    useEffect(() => {
        if (!run?.id) {
            return undefined;
        }
        let active = true;
        if (eventRunId.current !== run.id) {
            eventRunId.current = run.id;
            eventCursor.current = 0;
        }
        const refreshRun = async () => {
            try {
                const [runResponse, eventResponse, artifactResponse] = await Promise.all([
                    DataService.GetAgentRun(run.id),
                    eventStreamEnabled && !eventStreamFailed
                        ? Promise.resolve({ data: { events: [] } })
                        : DataService.GetAgentRunEvents(run.id, eventCursor.current),
                    DataService.GetAgentArtifacts(run.id),
                ]);
                if (!active) {
                    return;
                }
                const nextRun = runResponse?.data || run;
                let resultResponse = null;
                if (terminalRunStates.has(nextRun.state) && resultRunId.current !== run.id) {
                    resultRunId.current = run.id;
                    try {
                        resultResponse = await DataService.GetAgentRunResult(run.id);
                    } catch (resultError) {
                        if (resultError?.response?.status !== 404) {
                            throw resultError;
                        }
                    }
                }
                setRun(nextRun);
                if (resultResponse?.data) {
                    setResult(resultResponse.data);
                }
                const nextEvents = eventResponse?.data?.events || [];
                if (nextEvents.length) {
                    setEvents((current) => {
                        const mergedEvents = mergeRunEvents(current, nextEvents);
                        eventCursor.current = getLastRunEventSequence(mergedEvents);
                        return mergedEvents;
                    });
                }
                setArtifacts(artifactResponse?.data?.artifacts || []);
            } catch (requestError) {
                if (active) {
                    setError(requestError?.response?.data?.detail || 'Unable to refresh run status.');
                }
            }
        };
        refreshRun();
        const interval = setInterval(refreshRun, 5000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [eventStreamEnabled, eventStreamFailed, run?.id]);

    useEffect(() => {
        if (!run?.id || !eventStreamEnabled || eventStreamFailed || terminalRunStates.has(run.state)) {
            return undefined;
        }
        let active = true;
        let controller;
        let reconnectTimer;
        let streamFailures = 0;
        const reconnect = () => new Promise((resolve) => {
            reconnectTimer = setTimeout(resolve, 500);
        });
        const consumeEvents = async () => {
            while (active) {
                controller = new AbortController();
                try {
                    await DataService.StreamAgentRunEvents(
                        run.id,
                        eventCursor.current,
                        (nextEvent) => {
                            if (!active) {
                                return;
                            }
                            setEvents((current) => {
                                const mergedEvents = mergeRunEvents(current, [nextEvent]);
                                eventCursor.current = getLastRunEventSequence(mergedEvents);
                                return mergedEvents;
                            });
                        },
                        controller.signal,
                    );
                    streamFailures = 0;
                    if (active) {
                        await reconnect();
                    }
                } catch (streamError) {
                    if (!active) {
                        break;
                    }
                    streamFailures += 1;
                    if (streamFailures > MAX_EVENT_STREAM_RECONNECTS) {
                        setEventStreamFailed(true);
                        break;
                    }
                    await reconnect();
                }
            }
        };
        consumeEvents();
        return () => {
            active = false;
            clearTimeout(reconnectTimer);
            controller?.abort();
        };
    }, [eventStreamEnabled, eventStreamFailed, run?.id, run?.state]);

    const handleCancelRun = async () => {
        if (!run?.id) {
            return;
        }
        try {
            await DataService.CancelAgentRun(run.id, `cancel-${run.id}`);
            setRun((current) => ({ ...current, cancel_requested: true }));
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'Unable to acknowledge cancellation.');
        }
    };

    return (
        <div className={classes.root} data-testid="agent-workflow-panel">
            <div className={classes.scrollArea}>

            {serverConversationId && (
                <section className={classes.section} aria-labelledby="workflow-conversation-data-heading">
                    <Typography id="workflow-conversation-data-heading" variant="subtitle2">
                        Conversation data
                    </Typography>
                    <Typography variant="body2" className={classes.muted}>
                        Export a bounded JSON copy or permanently delete this server conversation. Recent interactive authentication is required for deletion.
                    </Typography>
                    <div className={classes.actions}>
                        <Button
                            variant="outlined"
                            onClick={handleExportConversation}
                            disabled={isExportingConversation || isDeletingConversation}
                        >
                            {isExportingConversation ? <CircularProgress size={18} /> : 'Export conversation'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleDeleteConversation}
                            disabled={isExportingConversation || isDeletingConversation}
                        >
                            {isDeletingConversation ? <CircularProgress size={18} /> : 'Delete conversation'}
                        </Button>
                    </div>
                </section>
            )}

            {uploads.length > 0 && (
            <section className={classes.section} aria-label="Attached uploads">
                <div className={classes.statusList}>
                    {uploads.map((upload) => (
                        <div className={classes.statusRow} key={upload.id}>
                            <Typography variant="body2">{upload.name}</Typography>
                            <Typography variant="caption" className={classes.muted}>
                                {upload.status === 'uploading' ? `${upload.progress}%` : upload.error || upload.status}
                            </Typography>
                        </div>
                    ))}
                </div>
            </section>
            )}

            {error && <Typography className={classes.error} role="alert">{error}</Typography>}
            {plan && (
                <section className={classes.section} aria-labelledby="workflow-plan-heading">
                    <Typography id="workflow-plan-heading" variant="subtitle2">
                        Plan status: {workflowStatusLabel(plan.status)}
                    </Typography>
                    <Typography variant="caption" className={classes.muted}>
                        Plan hash: {plan.content_hash || 'pending'} · approval is bound to exact hashes and bounds.
                    </Typography>
                    {estimatedBudget.length > 0 && (
                        <section className={classes.section} aria-labelledby="workflow-budget-heading">
                            <Typography id="workflow-budget-heading" variant="subtitle2">
                                Estimated resource bounds
                            </Typography>
                            <Typography variant="caption" className={classes.muted}>
                                Review these conservative plan estimates before approving the exact resource bounds below.
                            </Typography>
                            <div className={classes.statusList}>
                                {estimatedBudget.map((item) => (
                                    <div className={classes.statusRow} key={item.key}>
                                        <Typography variant="body2">{item.label}</Typography>
                                        <Typography variant="caption" className={classes.muted}>
                                            {item.key === 'max_cost_usd'
                                                ? `$${item.value.toFixed(4)} ${item.unit}`
                                                : `${item.value} ${item.unit}`}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                    {planUpperBounds.length > 0 && (
                        <section className={classes.section} aria-labelledby="workflow-plan-limits-heading">
                            <Typography id="workflow-plan-limits-heading" variant="subtitle2">
                                Plan maximum resource bounds
                            </Typography>
                            <Typography variant="caption" className={classes.muted}>
                                These are conservative plan ceilings, not optimistic performance estimates.
                            </Typography>
                            <div className={classes.statusList}>
                                {planUpperBounds.map((item) => (
                                    <div className={classes.statusRow} key={item.key}>
                                        <Typography variant="body2">{item.label}</Typography>
                                        <Typography variant="caption" className={classes.muted}>
                                            {item.key === 'max_cost_usd'
                                                ? `$${item.value.toFixed(4)} ${item.unit}`
                                                : `${item.value} ${item.unit}`}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                    <pre className={classes.plan}>{planPreview(plan.plan || plan)}</pre>
                    {isAgentWorkspaceEnabled() && (
                        <section className={classes.section} aria-labelledby="workflow-workspace-heading">
                            <Typography id="workflow-workspace-heading" variant="subtitle2">
                                Reviewed workspace
                            </Typography>
                            <Typography variant="caption" className={classes.muted}>
                                This opt-in step materializes only the attested tiny PINN template and runs bounded validation.
                            </Typography>
                            <div className={classes.actions}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleCreateReviewedWorkspace}
                                    disabled={isCreatingWorkspace || !!workspace}
                                >
                                    {isCreatingWorkspace ? <CircularProgress size={18} /> : workspace ? 'Reviewed workspace created' : 'Create reviewed workspace'}
                                </Button>
                                {workspace && (
                                    <Typography variant="caption" className={classes.muted}>
                                        Workspace validation: {workspaceValidation?.passed === true ? 'passed' : workspaceValidation?.passed === false ? 'failed' : 'pending'}
                                    </Typography>
                                )}
                            </div>
                            <ValidationReport
                                report={workspaceValidation}
                                classes={classes}
                                headingId="workspace-validation-heading"
                            />
                        </section>
                    )}
                    <div className={classes.fields}>
                        <TextField label="Code SHA-256" inputProps={{ 'data-testid': 'agent-code-hash' }} value={approvalFields.code_hash} onChange={updateApprovalField('code_hash')} />
                        <TextField label="Config SHA-256" inputProps={{ 'data-testid': 'agent-config-hash' }} value={approvalFields.config_hash} onChange={updateApprovalField('config_hash')} />
                        <TextField label="Dataset SHA-256 (optional)" value={approvalFields.dataset_hash} onChange={updateApprovalField('dataset_hash')} />
                        <TextField label="Image digest (optional)" value={approvalFields.image_digest} onChange={updateApprovalField('image_digest')} />
                    </div>
                    <TextField label="Resource bounds JSON" value={approvalFields.bounds} onChange={updateApprovalField('bounds')} fullWidth />
                    <div className={classes.actions}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleApprovePlan}
                            disabled={!approvalFields.code_hash.trim() || !approvalFields.config_hash.trim() || isApproving || !!approval}
                        >
                            {isApproving ? <CircularProgress size={18} /> : approval ? 'Plan approved' : 'Approve exact plan'}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleStartRun}
                            disabled={!isTrainingEnabled() || !approval || isStarting}
                        >
                            {isStarting ? <CircularProgress size={18} /> : 'Start bounded run'}
                        </Button>
                    </div>
                    {!isTrainingEnabled() && (
                        <Typography variant="caption" className={classes.muted}>
                            Start is disabled because TRAINING_ENABLED is false. Approval alone cannot provision infrastructure.
                        </Typography>
                    )}
                    {isStepUpRequired && (
                        <StepUpPrompt
                            classes={classes}
                            error={stepUpError}
                            isSubmitting={isStepUpSubmitting}
                            authMethod={authMethod}
                            onCancel={() => {
                                setIsStepUpRequired(false);
                                setStepUpError('');
                            }}
                            onConfirm={handleStepUp}
                            onGoogleConfirm={handleGoogleStepUp}
                        />
                    )}
                </section>
            )}

            {run && (
                <section className={classes.section} aria-labelledby="workflow-run-heading">
                    <Typography id="workflow-run-heading" variant="subtitle2">
                        Run: {workflowStatusLabel(run.state)}
                    </Typography>
                    <div className={classes.actions}>
                        <Typography variant="caption" className={classes.muted}>
                            {run.cancel_requested_at || run.cancel_requested ? 'Cancellation requested' : 'Status refresh resumes from the last event.'}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleCancelRun}
                            disabled={!!run.cancel_requested_at || !!run.cancel_requested || terminalRunStates.has(run.state)}
                        >
                            Request cancellation
                        </Button>
                    </div>
                    <div className={classes.statusList} aria-live="polite">
                        {events.map((event) => (
                            <div className={classes.statusRow} key={`${event.sequence}-${event.to_state}`}>
                                <Typography variant="body2">
                                    #{event.sequence} {workflowStatusLabel(event.to_state)}
                                </Typography>
                                <Typography variant="caption" className={classes.muted}>{event.actor}</Typography>
                            </div>
                        ))}
                    </div>
                    <ValidationReport report={result?.validation || run.validation_report || run.validation} classes={classes} />
                    {result && (
                        <section className={classes.section} aria-labelledby="workflow-result-heading">
                            <Typography id="workflow-result-heading" variant="subtitle2">Run result</Typography>
                            {typeof result.text_summary === 'string' && (
                                <Typography variant="body2">{result.text_summary.slice(0, 512)}</Typography>
                            )}
                            {Array.isArray(result.skipped_graphs) && result.skipped_graphs.length > 0 && (
                                <Typography variant="caption" className={classes.muted}>
                                    {result.skipped_graphs.length} graph fallback(s) unavailable.
                                </Typography>
                            )}
                        </section>
                    )}
                    <MetricsPanel graphs={result?.graphs || run.graphs || run.metrics?.graphs} classes={classes} />
                    {artifacts.length > 0 && (
                        <section className={classes.section} aria-labelledby="workflow-artifacts-heading">
                            <Typography id="workflow-artifacts-heading" variant="subtitle2">Artifacts</Typography>
                            <div className={classes.statusList}>
                                {artifacts.map((artifact) => {
                                    const presentation = artifactPresentation(artifact);
                                    const sizeLabel = Number.isFinite(artifact.size_bytes)
                                        ? `${artifact.size_bytes} bytes`
                                        : 'bounded artifact';
                                    return (
                                        <div className={classes.statusRow} key={artifact.id}>
                                            <div>
                                                <Typography variant="body2">{presentation.label}</Typography>
                                                {presentation.caption && (
                                                    <Typography variant="caption" className={classes.muted}>
                                                        {presentation.caption}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" className={classes.muted}>
                                                    {sizeLabel} · {artifactRetentionLabel(artifact.retention_expires_at)}
                                                </Typography>
                                            </div>
                                            <Button
                                                variant="outlined"
                                                onClick={() => handleDownloadArtifact(artifact)}
                                                disabled={!!artifactDownloadId}
                                            >
                                                {artifactDownloadId === artifact.id
                                                    ? <CircularProgress size={18} />
                                                    : `Download ${presentation.label}`}
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}
                </section>
            )}
            </div>

            <form className={classes.composerArea} onSubmit={handleCreatePlan}>
                <div className={classes.composerShell}>
                    {mentionQuery !== null && mentionSuggestions.length > 0 && (
                        <div className={classes.mentionMenu} role="listbox" aria-label="File suggestions">
                            {mentionSuggestions.map((file, index) => (
                                <Button
                                    key={file.id}
                                    type="button"
                                    role="option"
                                    aria-selected={index === mentionIndex}
                                    className={index === mentionIndex
                                        ? `${classes.mentionOption} ${classes.mentionOptionActive}`
                                        : classes.mentionOption}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => selectMention(file)}
                                >
                                    <InsertDriveFileRoundedIcon fontSize="small" className={classes.mentionIcon} />
                                    <span className={classes.mentionDetails}>
                                        <Typography variant="body2" className={classes.mentionName}>{file.filename}</Typography>
                                        <Typography variant="caption" color="textSecondary">Use this private file</Typography>
                                    </span>
                                </Button>
                            ))}
                        </div>
                    )}
                    <div className={classes.composer}>
                    <TextField
                        value={requirements}
                        onChange={handleRequirementsChange}
                        onKeyDown={handleRequirementsKeyDown}
                        inputRef={composerInputRef}
                        multiline
                        minRows={1}
                        maxRows={6}
                        variant="standard"
                        placeholder="Describe your PINN problem or planning request…"
                        inputProps={{ maxLength: 12000 }}
                        InputProps={{ disableUnderline: true }}
                        fullWidth
                    />
                    <Tooltip title="Attach">
                        <span>
                            <IconButton
                                className={classes.composerIcon}
                                component="label"
                                color="primary"
                                size="small"
                                aria-label="Attach"
                                disabled={isPlanning || isCreatingConversation}
                            >
                                <AttachFileRoundedIcon />
                                <input type="file" hidden multiple onChange={handleUploadChange} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Send">
                        <span>
                            <IconButton
                                className={classes.composerIcon}
                                color="primary"
                                size="small"
                                type="submit"
                                aria-label="Create reviewable plan"
                                disabled={!requirements.trim() || isPlanning || isCreatingConversation || !conversationId}
                            >
                                {isPlanning || isCreatingConversation ? <CircularProgress size={22} /> : <SendRoundedIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    </div>
                </div>
            </form>
        </div>
    );
};

export { AgentWorkflowPanel, artifactPresentation, budgetPresentation, planPreview };
export default withStyles(styles)(AgentWorkflowPanel);
